import React, { useEffect, useRef } from "react";

/**
 * A1-CAPTCHA — Cloudflare Turnstile widget.
 *
 * Shown on the login form after N failed attempts (the backend returns
 * `captcha_required` + a `sitekey`). Loads the Turnstile script once, renders
 * the widget, and hands the solved token up via `onToken`. Renders nothing
 * until a `sitekey` is present, so it stays inert on a normal login.
 *
 * `onError` fires if the challenge can't be shown — the script is blocked
 * (ad-blocker / proxy / CSP), never loads within the timeout, or Turnstile
 * reports an error. The login form uses it to tell the user what to do instead
 * of leaving them stuck behind a widget that will never appear.
 */
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";
const RENDER_TIMEOUT_MS = 8000;

function ensureScript(onError) {
  if (typeof document === "undefined") return;
  if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) return;
  const s = document.createElement("script");
  s.src = SCRIPT_SRC;
  s.async = true;
  s.defer = true;
  if (onError) s.onerror = () => onError();
  document.head.appendChild(s);
}

export default function TurnstileWidget({ sitekey, onToken, onError }) {
  const ref = useRef(null);
  const widgetId = useRef(null);

  useEffect(() => {
    if (!sitekey) return undefined;
    ensureScript(onError);
    let cancelled = false;
    let waited = 0;
    const render = () => {
      if (cancelled) return;
      const ts = typeof window !== "undefined" ? window.turnstile : null;
      if (!ts || !ref.current) {
        waited += 200;
        if (waited >= RENDER_TIMEOUT_MS) {
          // Script blocked or never loaded — surface it, don't trap the user.
          if (onError) onError();
          return;
        }
        setTimeout(render, 200);
        return;
      }
      if (widgetId.current !== null) return; // already rendered
      widgetId.current = ts.render(ref.current, {
        sitekey,
        callback: (token) => onToken && onToken(token),
        "error-callback": () => {
          if (onToken) onToken("");
          if (onError) onError();
        },
        "expired-callback": () => onToken && onToken(""),
      });
    };
    render();
    return () => {
      cancelled = true;
      const ts = typeof window !== "undefined" ? window.turnstile : null;
      if (ts && widgetId.current !== null) {
        try {
          ts.remove(widgetId.current);
        } catch {
          /* widget already gone — noop */
        }
        widgetId.current = null;
      }
    };
  }, [sitekey, onToken, onError]);

  if (!sitekey) return null;
  return <div ref={ref} data-testid="turnstile-widget" style={{ margin: "4px 0" }} />;
}
